// src/components/SynergySuggestions.tsx - Card Synergy Suggestion Component (Fixed)

import React, { useState } from 'react';
import { Card } from '../types';

interface SynergySuggestionsProps {
  currentDeckCards: Card[];
  playerCards: Card[];
  allCards: Card[];
  onCardSelect?: (card: Card) => void;
  onShopRedirect?: () => void;
}

interface Synergy {
  cardId: number;
  suggestedCardIds: number[];
  reason: string;
  category: 'offense' | 'defense' | 'utility' | 'combo';
}

// Synergy configuration - easily expandable for new cards
// Complete synergy configuration - paste this over the existing CARD_SYNERGIES array
const CARD_SYNERGIES: Synergy[] = [
  // ========== DEFENSE MANIPULATION COMBOS ==========
  {
    cardId: 23, // The Administrator
    suggestedCardIds: [39, 6, 24, 1], // Zero Trust Protocol, Security Patch, Multifactor Auth, Network Firewall
    reason: "Build massive defense then convert to damage with Zero Trust Protocol",
    category: 'combo'
  },
  {
    cardId: 39, // Zero Trust Protocol
    suggestedCardIds: [23, 6, 24, 1, 25], // The Administrator, Security Patch, Multifactor Auth, Network Firewall, Home Lab Engineer
    reason: "Build defense first to maximize damage conversion",
    category: 'combo'
  },
  {
    cardId: 6, // Security Patch
    suggestedCardIds: [39, 36, 25], // Zero Trust Protocol, Incident Commander, Home Lab Engineer
    reason: "Permanent defense synergizes with defense-based strategies",
    category: 'defense'
  },

  // ========== INCIDENT COMMANDER SYNERGIES ==========
  {
    cardId: 36, // Incident Commander
    suggestedCardIds: [23, 24, 1, 6, 4, 5, 31, 33], // The Administrator, Multifactor Auth, Network Firewall, Security Patch, Encryption Shield, Intrusion Detection, Air Gap, Honeypot
    reason: "Deal 10 damage when opponent has 5+ defense - encourage them to defend first",
    category: 'offense'
  },

  // ========== LOW HEALTH / RISK SYNERGIES ==========
  {
    cardId: 13, // Desperate Strike
    suggestedCardIds: [32, 10, 2, 40], // Disaster Recovery, Incident Response, Backup Server, CTRL+Z
    reason: "Healing cards help you survive to use low-health attacks",
    category: 'utility'
  },
  {
    cardId: 32, // Disaster Recovery
    suggestedCardIds: [13, 38, 40], // Desperate Strike, Insider Threat, CTRL+Z
    reason: "Use risky/low-health cards knowing you can recover",
    category: 'utility'
  },
  {
    cardId: 38, // Insider Threat
    suggestedCardIds: [32, 10, 2, 40], // Disaster Recovery, Incident Response, Backup Server, CTRL+Z
    reason: "Self-damage risk mitigated by healing options",
    category: 'utility'
  },

  // ========== HOME LAB ENGINEER SYNERGIES ==========
  {
    cardId: 25, // Home Lab Engineer
    suggestedCardIds: [1, 24, 4, 5, 31, 33, 35, 34], // Network Firewall, Multifactor Auth, Encryption Shield, Intrusion Detection, Air Gap, Honeypot, SAAT Training, Security Audit
    reason: "Gets +2 defense for each defensive card played this match",
    category: 'defense'
  },

  // ========== HIGH-HEALTH DAMAGE SYNERGIES ==========
  {
    cardId: 12, // Spear Phishing (+2 if opponent has 5+ health)
    suggestedCardIds: [8], // Database Corruption
    reason: "Both deal bonus damage vs healthy opponents - use early in match",
    category: 'offense'
  },
  {
    cardId: 8, // Database Corruption (+2 if opponent has 15+ health)
    suggestedCardIds: [12], // Spear Phishing
    reason: "Both deal bonus damage vs healthy opponents - use early in match",
    category: 'offense'
  },

  // ========== DEFENSE-BASED OFFENSE SYNERGIES ==========
  {
    cardId: 27, // Privilege Escalation
    suggestedCardIds: [36, 26], // Incident Commander, Reboot
    reason: "Both get stronger when opponent has high defense, or remove their defense first",
    category: 'offense'
  },
  {
    cardId: 26, // Reboot
    suggestedCardIds: [27, 36], // Privilege Escalation, Incident Commander
    reason: "Remove all opponent defense, then exploit their vulnerability",
    category: 'combo'
  },

  // ========== CONTROL/DISRUPTION COMBOS ==========
  {
    cardId: 28, // Supply Chain Attack
    suggestedCardIds: [30, 41, 37], // Memory Corruption, Progressive Ransomware, Red Team Exercise
    reason: "Nullify opponent's next attack while building advantage",
    category: 'combo'
  },
  {
    cardId: 30, // Memory Corruption
    suggestedCardIds: [28, 41], // Supply Chain Attack, Progressive Ransomware
    reason: "Swap opponent's card values then disrupt their strategy",
    category: 'combo'
  },
  {
    cardId: 41, // Progressive Ransomware
    suggestedCardIds: [37, 28], // Red Team Exercise, Supply Chain Attack
    reason: "Weaken attacks while scaling damage with their card count",
    category: 'combo'
  },

  // ========== PIERCING DAMAGE COMBOS ==========
  {
    cardId: 7, // Social Engineering
    suggestedCardIds: [3, 9, 35], // DDoS Attack, Zero Day, SAAT Training
    reason: "Bypass defensive effects - combine with other piercing attacks",
    category: 'offense'
  },
  {
    cardId: 3, // DDoS Attack
    suggestedCardIds: [7, 9], // Social Engineering, Zero Day
    reason: "Cannot be reduced - combine with other piercing attacks",
    category: 'offense'
  },
  {
    cardId: 9, // Zero Day
    suggestedCardIds: [3, 7], // DDoS Attack, Social Engineering
    reason: "Piercing damage combo - defense cannot stop this assault",
    category: 'offense'
  },

  // ========== DEFENSIVE STACKING SYNERGIES ==========
  {
    cardId: 1, // Network Firewall
    suggestedCardIds: [24, 31], // Multifactor Authentication, Air Gap Protocol
    reason: "Stack complete attack blocking effects for total protection",
    category: 'defense'
  },
  {
    cardId: 24, // Multifactor Authentication
    suggestedCardIds: [1, 31], // Network Firewall, Air Gap Protocol
    reason: "Multiple block effects provide sustained protection",
    category: 'defense'
  },
  {
    cardId: 31, // Air Gap Protocol
    suggestedCardIds: [1, 24, 33], // Network Firewall, Multifactor Auth, Honeypot Trap
    reason: "Ultimate defense - blocks any amount of damage",
    category: 'defense'
  },
  {
    cardId: 4, // Encryption Shield
    suggestedCardIds: [5, 33], // Intrusion Detection, Honeypot Trap
    reason: "Stack damage reduction with counter-attack effects",
    category: 'defense'
  },
  {
    cardId: 5, // Intrusion Detection
    suggestedCardIds: [4, 33], // Encryption Shield, Honeypot Trap
    reason: "Counter-attack combined with damage reduction",
    category: 'defense'
  },
  {
    cardId: 33, // Honeypot Trap
    suggestedCardIds: [4, 5, 31], // Encryption Shield, Intrusion Detection, Air Gap Protocol
    reason: "Punish attackers while defending yourself",
    category: 'defense'
  },

  // ========== HEALING SYNERGIES ==========
  {
    cardId: 2, // Backup Server
    suggestedCardIds: [10, 32], // Incident Response, Disaster Recovery
    reason: "Stack healing effects for sustained survival",
    category: 'utility'
  },
  {
    cardId: 10, // Incident Response
    suggestedCardIds: [2, 32], // Backup Server, Disaster Recovery
    reason: "Conditional healing works well with other healing options",
    category: 'utility'
  },

  // ========== DEFENSE STEALING/MANIPULATION ==========
  {
    cardId: 42, // Smalsy
    suggestedCardIds: [39, 36], // Zero Trust Protocol, Incident Commander
    reason: "Steal opponent's defense, then convert your own or exploit theirs",
    category: 'combo'
  },

  // ========== EMERGENCY/UNDO SYNERGIES ==========
  {
    cardId: 40, // CTRL+Z
    suggestedCardIds: [38, 13, 28], // Insider Threat, Desperate Strike, Supply Chain Attack
    reason: "Undo damage from risky plays or devastating opponent attacks",
    category: 'utility'
  },

  // ========== SAAT TRAINING SYNERGIES ==========
  {
    cardId: 35, // SAAT Training
    suggestedCardIds: [7, 25], // Social Engineering (counter), Home Lab Engineer
    reason: "Immunity to bypass effects and defensive synergy",
    category: 'defense'
  },

  // ========== SECURITY AUDIT SYNERGIES ==========
  {
    cardId: 34, // Security Audit
    suggestedCardIds: [36, 27, 39], // Incident Commander, Privilege Escalation, Zero Trust Protocol
    reason: "Reveal opponent defense to optimize defense-based strategies",
    category: 'defense'
  },

  // ========== RED TEAM EXERCISE SYNERGIES ==========
  {
    cardId: 37, // Red Team Exercise
    suggestedCardIds: [41, 28], // Progressive Ransomware, Supply Chain Attack
    reason: "Scale damage as match progresses while controlling opponent",
    category: 'offense'
  },

  // ========== ONCE-PER-MATCH TIMING SYNERGIES ==========
  {
    cardId: 11, // Logic Bomb
    suggestedCardIds: [9, 22, 28, 30], // Zero Day, AI Sentinel, Supply Chain Attack, Memory Corruption
    reason: "Coordinate once-per-match power plays for maximum impact",
    category: 'offense'
  },
  {
    cardId: 22, // AI Sentinel
    suggestedCardIds: [9, 11, 28, 30, 32, 40], // Zero Day, Logic Bomb, Supply Chain Attack, Memory Corruption, Disaster Recovery, CTRL+Z
    reason: "Ultimate late-game power - coordinate with other unique effects",
    category: 'utility'
  },

  // ========== BASIC ATTACK SCALING ==========
  {
    cardId: 14, // Virus Infection
    suggestedCardIds: [15, 16, 17], // Port Scan, Brute Force, Malware Deploy
    reason: "Basic attack combo - reliable damage progression",
    category: 'offense'
  },
  {
    cardId: 15, // Port Scan
    suggestedCardIds: [14, 16, 17], // Virus Infection, Brute Force, Malware Deploy
    reason: "Early game pressure with basic attacks",
    category: 'offense'
  },
  {
    cardId: 16, // Brute Force
    suggestedCardIds: [14, 15, 17], // Virus Infection, Port Scan, Malware Deploy
    reason: "Consistent damage - simple but effective",
    category: 'offense'
  },

  // ========== HIGH-DAMAGE FINISHERS ==========
  {
    cardId: 18, // Ransomware
    suggestedCardIds: [19, 20, 21], // Data Breach, System Takeover, Infrastructure Attack
    reason: "High-damage finisher combo for closing out games",
    category: 'offense'
  },
  {
    cardId: 19, // Data Breach
    suggestedCardIds: [18, 20, 21], // Ransomware, System Takeover, Infrastructure Attack
    reason: "Mid-to-high damage range - consistent threat",
    category: 'offense'
  },
  {
    cardId: 20, // System Takeover
    suggestedCardIds: [18, 19, 21], // Ransomware, Data Breach, Infrastructure Attack
    reason: "Heavy damage options for aggressive strategies",
    category: 'offense'
  },
  {
    cardId: 21, // Infrastructure Attack
    suggestedCardIds: [18, 19, 20], // Ransomware, Data Breach, System Takeover
    reason: "Maximum basic damage - perfect finisher",
    category: 'offense'
  }
];

const SynergySuggestions: React.FC<SynergySuggestionsProps> = ({
  currentDeckCards,
  playerCards,
  allCards,
  onCardSelect,
  onShopRedirect
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Get synergies for cards currently in deck
  const getRelevantSynergies = () => {
    const deckCardIds = new Set(currentDeckCards.map(card => card.id));
    const ownedCardIds = new Set(playerCards.map(card => card.id));
    
    const suggestions: Array<{
      triggerCard: Card;
      suggestedCard: Card;
      reason: string;
      category: string;
      owned: boolean;
    }> = [];

    currentDeckCards.forEach(deckCard => {
      const synergies = CARD_SYNERGIES.filter(s => s.cardId === deckCard.id);
      
      synergies.forEach(synergy => {
        synergy.suggestedCardIds.forEach(suggestedId => {
          // Don't suggest cards already in deck
          if (deckCardIds.has(suggestedId)) return;
          
          const suggestedCard = allCards.find(card => card.id === suggestedId);
          if (!suggestedCard) return;

          suggestions.push({
            triggerCard: deckCard,
            suggestedCard,
            reason: synergy.reason,
            category: synergy.category,
            owned: ownedCardIds.has(suggestedId)
          });
        });
      });
    });

    // Remove duplicates and sort by owned first, then by category
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
      index === self.findIndex(s => s.suggestedCard.id === suggestion.suggestedCard.id)
    );

    return uniqueSuggestions.sort((a, b) => {
      if (a.owned !== b.owned) return a.owned ? -1 : 1; // Owned first
      return a.category.localeCompare(b.category);
    });
  };

  const suggestions = getRelevantSynergies();

  if (suggestions.length === 0) {
    return null; // No suggestions to show
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'offense': return '⚔️';
      case 'defense': return '🛡️';
      case 'utility': return '🔧';
      case 'combo': return '⚡';
      default: return '✨';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'offense': return 'text-red-400';
      case 'defense': return 'text-blue-400';
      case 'utility': return 'text-green-400';
      case 'combo': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 overflow-hidden flex flex-col max-h-full">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 bg-gray-750 hover:bg-gray-700 transition-colors flex items-center justify-between flex-shrink-0"
      >
        <div className="flex items-center gap-2">
          <span className="text-purple-400">✨</span>
          <h4 className="font-semibold text-white">Synergy Suggestions</h4>
          <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
            {suggestions.length}
          </span>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content - Horizontal scrolling for full-width layout */}
      {isExpanded && (
        <div className="p-4 flex gap-4 overflow-x-auto overflow-y-hidden flex-shrink-0" style={{ maxHeight: '180px' }}>
          {suggestions.map((suggestion, index) => (
            <div 
              key={`${suggestion.triggerCard.id}-${suggestion.suggestedCard.id}`}
              className="bg-gray-700 rounded-lg p-3 border border-gray-600 flex-shrink-0"
              style={{ minWidth: '300px', maxWidth: '300px' }}
            >
              {/* Suggestion Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={getCategoryIcon(suggestion.category)}></span>
                  <span className={`text-sm font-medium ${getCategoryColor(suggestion.category)}`}>
                    {suggestion.category.toUpperCase()}
                  </span>
                  {suggestion.owned ? (
                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">OWNED</span>
                  ) : (
                    <span className="bg-yellow-600 text-black text-xs px-2 py-1 rounded">SHOP</span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  {suggestion.suggestedCard.rarity}
                </span>
              </div>

              {/* Card Suggestion */}
              <div className="mb-2">
                <div className="text-sm text-gray-300">
                  <span className="text-blue-300 font-medium">{suggestion.triggerCard.name}</span>
                  <span className="text-gray-400 mx-2">+</span>
                  <span className="text-yellow-300 font-medium">{suggestion.suggestedCard.name}</span>
                </div>
              </div>

              {/* Reason */}
              <p className="text-xs text-gray-400 mb-3">{suggestion.reason}</p>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {suggestion.owned ? (
                  <button
                    onClick={() => onCardSelect?.(suggestion.suggestedCard)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs py-2 px-3 rounded transition-colors"
                  >
                    Add to Deck
                  </button>
                ) : (
                  <button
                    onClick={onShopRedirect}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-black text-xs py-2 px-3 rounded transition-colors font-medium"
                  >
                    Buy for {suggestion.suggestedCard.price} CC
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SynergySuggestions;